import { Camera } from 'lucide-react';
// apps/web/src/pages/coletor/leitura-page.tsx — Fase 02, Plano 07 (OPS-06, APT-02, D-09).
import { useState, useEffect, type ReactElement } from 'react';
import { attachUsbReader } from '../../components/coletor/usb-reader';
import { QrScanner } from '../../components/coletor/qr-scanner';
import { ColetorBarraIdentidade } from '../../components/coletor/coletor-identidade';
import { montarUrlApontamento } from '../../lib/coletor-url';

export type EntidadeIdentificada =
  | {
      readonly tipo: 'OS';
      readonly id: string;
      readonly opId: string;
      readonly etapaOrdem: number;
      readonly celulaId: string;
      readonly status: string;
    }
  | {
      readonly tipo: 'OP';
      readonly id: string;
      readonly numero: number;
      readonly status: string;
      readonly loteId: string;
    }
  | {
      readonly tipo: 'LOTE';
      readonly id: string;
      readonly loteInternoPieltec: string;
      readonly itemId: string;
      readonly uso: string;
    };

export function LeituraPage(): ReactElement {
  const [scannerOpen, setScannerOpen] = useState<boolean>(false);
  const [identificando, setIdentificando] = useState<boolean>(false);
  const [entidade, setEntidade] = useState<EntidadeIdentificada | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Resolver identificação
  const processarScan = async (scannedId: string) => {
    setScannerOpen(false);
    setIdentificando(true);
    setError(null);
    try {
      const res = await fetch(`/api/v1/producao/identificar/${encodeURIComponent(scannedId.trim())}`);
      if (!res.ok) {
        throw new Error('Código identificador não encontrado ou não cadastrado no sistema.');
      }
      const data = (await res.json()) as EntidadeIdentificada;
      setEntidade(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao identificar código');
    } finally {
      setIdentificando(false);
    }
  };

  // Listener do Leitor USB ativo em paralelo
  useEffect(() => {
    const cleanup = attachUsbReader({
      onScan: (text) => {
        void processarScan(text);
      },
    });
    return cleanup;
  }, []);

  const handleIrParaApontamento = () => {
    if (!entidade) return;
    if (entidade.tipo === 'OS') {
      window.location.href = montarUrlApontamento({
        osId: entidade.id,
        etapaOrdem: String(entidade.etapaOrdem),
        celulaId: entidade.celulaId,
      });
      return;
    }
    // OP e lote não abrem apontamento sozinhos — a OS é a unidade de apontamento.
    window.location.href = '/coletor/fila';
  };

  return (
    <div
      className="leitura-page min-h-screen bg-slate-950 text-slate-100 p-4 md:p-8 flex flex-col justify-between select-none"
      data-theme="dark-premium"
      data-density="touch"
    >
      <div className="max-w-md mx-auto w-full space-y-6">
        <ColetorBarraIdentidade />
        <header className="text-center space-y-2 pt-2">
          <span className="inline-block px-3 py-1 bg-emerald-700/20 border border-emerald-500/40 text-emerald-300 text-xs font-bold rounded-full uppercase tracking-wider">
            Coletor
          </span>
          <h1 className="text-3xl font-bold text-white tracking-tight">Identificar produção</h1>
          <p className="text-xs text-slate-400">
            Aproxime o leitor USB ou use a câmera do tablet para ler a etiqueta.
          </p>
        </header>

        {error && (
          <div className="p-4 bg-red-950/80 border border-red-500/50 text-red-200 rounded-xl text-xs font-medium text-center shadow-lg" role="alert">
            {error}
          </div>
        )}

        {identificando && (
          <div className="p-8 text-center bg-slate-900 border border-slate-800 rounded-2xl">
            <span className="text-sm text-emerald-300 font-bold animate-pulse">Identificando QR Code no sistema...</span>
          </div>
        )}

        {/* Tela de Confirmação Explícita (D-09) */}
        {entidade ? (
          <div className="bg-slate-900 border border-slate-750 rounded-2xl p-6 space-y-5 shadow-2xl animate-fade-in">
            <div className="text-center border-b border-slate-800 pb-4">
              <span className="text-xs font-bold uppercase tracking-widest text-emerald-400">
                Entidade Identificada com Sucesso
              </span>
              <h2 className="text-2xl font-bold text-white mt-1">
                {entidade.tipo === 'OS' && `Ordem de Serviço #${entidade.etapaOrdem}`}
                {entidade.tipo === 'OP' && `Ordem de Produção OP-${entidade.numero}`}
                {entidade.tipo === 'LOTE' && `Lote ${entidade.loteInternoPieltec}`}
              </h2>
            </div>

            <div className="space-y-2 text-xs bg-slate-950/60 p-4 rounded-xl border border-slate-800/80 font-mono">
              {entidade.tipo === 'OS' && (
                <>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Célula / Posto:</span>
                    <span className="text-slate-200 font-bold">{entidade.celulaId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-400">Status da OS:</span>
                    <span className="text-emerald-300 font-bold">{entidade.status}</span>
                  </div>
                </>
              )}
              {entidade.tipo === 'OP' && (
                <div className="flex justify-between">
                  <span className="text-slate-400">Status da OP:</span>
                  <span className="text-emerald-300 font-bold">{entidade.status}</span>
                </div>
              )}
              {entidade.tipo === 'LOTE' && (
                <div className="flex justify-between">
                  <span className="text-slate-400">Item / Material:</span>
                  <span className="text-slate-200 font-bold">{entidade.itemId}</span>
                </div>
              )}
            </div>

            <p className="text-[11px] text-amber-300/90 text-center bg-amber-950/20 p-2.5 rounded-lg border border-amber-500/20">
              A leitura não inicia a contagem. Confirme abaixo para abrir o apontamento desta OS.
            </p>

            <div className="space-y-3 pt-2">
              <button
                type="button"
                onClick={handleIrParaApontamento}
                className="w-full min-h-[64px] py-4 bg-emerald-600 hover:bg-emerald-500 active:bg-emerald-700 text-white font-bold text-base rounded-xl shadow-xl transition uppercase tracking-wider touch-manipulation"
              >
                {entidade.tipo === 'OS'
                  ? 'Confirmar e abrir apontamento'
                  : 'Abrir fila para escolher a OS'}
              </button>
              <button
                type="button"
                onClick={() => setEntidade(null)}
                className="w-full min-h-[48px] py-2 border border-slate-700 hover:bg-slate-800 text-slate-300 font-medium text-xs rounded-xl"
              >
                Ler Outro Código
              </button>
            </div>
          </div>
        ) : (
          /* Botões de Ação de Leitura */
          <div className="space-y-4 pt-4">
            <button
              type="button"
              onClick={() => setScannerOpen(true)}
              className="w-full min-h-[72px] py-5 bg-emerald-700 hover:bg-emerald-600 active:bg-emerald-800 text-white font-bold text-lg rounded-2xl shadow-xl transition uppercase tracking-wider flex items-center justify-center gap-3 touch-manipulation focus:ring-4 focus:ring-emerald-400/50"
            >
              <Camera size={26} aria-hidden="true" /> Ler QR Code (Câmera)
            </button>

            <div className="p-4 bg-slate-900/60 border border-slate-800 rounded-xl text-center space-y-1">
              <p className="text-xs font-semibold text-slate-300">Leitor USB HID</p>
              <p className="text-[11px] text-slate-500">
                Se o leitor estiver ligado, dispare a etiqueta a qualquer momento nesta tela.
              </p>
            </div>

            <div className="text-center pt-2">
              <a
                href="/coletor/fila"
                className="text-xs text-emerald-300 hover:text-blue-300 underline font-medium p-3 inline-block min-h-[44px]"
              >
                Buscar OS na fila da célula
              </a>
            </div>
          </div>
        )}

        {/* Modal de Câmera */}
        {scannerOpen && (
          <QrScanner
            onScan={(text) => void processarScan(text)}
            onClose={() => setScannerOpen(false)}
          />
        )}
      </div>

      <footer className="text-center text-[10px] text-slate-600 py-4">
        Pieltec MES · Coletor
      </footer>
    </div>
  );
}
